import { TOOL_NAMES } from 'js/src/agent/toolNames';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import { IconRegistry } from '../registries/IconRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { isNonRecommendable } from '../video-state/serialization/assetSerializer';

import {
    applyResolvedAssetTimingOperation,
    hasAssetTimingArgs,
    resolveAssetTimingOperation,
} from './asset-timing-placement';
import { ASSET_TIMING_SCHEMA_SHAPE } from './asset-timing-schema';
import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { checkpointVideo } from 'js/src/data/video-data';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setIconSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. icon_1)'),
    media_id: z
        .string()
        .optional()
        .describe(
            'The ID of the media to apply to the icon asset. Must be an ID from search results.',
        ),
    ...ASSET_TIMING_SCHEMA_SHAPE,
});
export type SetIconArgs = z.infer<typeof setIconSchema>;

export class SetIconTool extends VideoStateTool<typeof setIconSchema> {
    protected override readonly schema = setIconSchema;
    protected override readonly toolName = TOOL_NAMES.setIcon;
    protected override readonly toolDescription = 'Set icon for a specific scene and asset';

    public override customRenderer = SetIconResultRenderer;

    public override getToolDisplayName(): string {
        return 'Setting icon';
    }

    public get statusIcon(): string {
        return 'emoji_symbols';
    }

    public async execute(args: SetIconArgs, context: ToolExecutionContext) {
        const { asset_name, media_id } = args;
        const { parentActionGroup } = context;
        const timingRequested = hasAssetTimingArgs(args);

        const changes: string[] = [];
        try {
            if (media_id === undefined && !timingRequested) {
                return 'Error: At least one of media_id, start_words or start_time_ms must be provided';
            }

            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }
            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            if (isNonRecommendable(scene, asset_name, this.getState())) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            if (!VideoData.isIconAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not an icon asset`;
            }

            // Resolve timing before any edit lands, so a bad start_words / start_time_ms rejects
            // the whole call instead of leaving the icon swapped and the timing not.
            const { resolveAssetTiming, applyResolvedAssetTiming } = makeOperationsCallable(
                { resolveAssetTimingOperation, applyResolvedAssetTimingOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );
            let resolvedTiming: { introTime: number; exitTime: number } | undefined;
            if (timingRequested) {
                const resolved = resolveAssetTiming(scene, asset, args);
                if ('error' in resolved) {
                    return `Error: ${resolved.error}`;
                }
                resolvedTiming = resolved;
            }

            if (media_id !== undefined) {
                const iconRegistry = IconRegistry.getInstance();
                if (!iconRegistry.hasItem(media_id)) {
                    return `Error: Unknown icon ${media_id}`;
                }

                const icon_source_data = iconRegistry.getIconSourceData(media_id);
                if (!icon_source_data) {
                    return `Error: Unknown icon ${media_id}`;
                }

                const alreadyApplied = asset.sourceData?.url === icon_source_data.url;
                if (alreadyApplied && !timingRequested) {
                    return `Error: Asset ${asset_name} in scene ${sceneShortId} already uses icon ${media_id} — nothing changed. Pick a different icon, or leave the asset as is.`;
                }

                if (!alreadyApplied) {
                    try {
                        asset.replace(icon_source_data);
                    } catch (error) {
                        return `Error: data wrong format for asset ${asset_name} in scene ${sceneShortId}.`;
                    }
                    changes.push(`set media for ${asset_name} to ${media_id}`);
                }
            }

            if (changes.length > 0) {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                this.recordSceneFill(scene_index, 'icon');
            }

            // Timing last: it checkpoints the video itself, so the swapped icon is already on the
            // asset by the time it saves.
            if (resolvedTiming) {
                const applied = applyResolvedAssetTiming(
                    scene,
                    asset,
                    resolvedTiming,
                    args,
                    parentActionGroup,
                );
                changes.push('warning' in applied ? applied.warning : applied.change);
            }

            return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${sceneShortId}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting media: ${errorMessage}`;
        }
    }
}

const SetIconResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const mediaId = args.media_id;
    const iconRegistry = IconRegistry.getInstance();
    const iconData = mediaId ? iconRegistry.getItem(mediaId) : null;
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
            {iconData && (
                <div>
                    {React.createElement(BackgroundAsset, {
                        searchContext: null,
                        media: ImmutableFromJS(iconData),
                        media_id: 0,
                        result_index: 0,
                    })}
                </div>
            )}
        </div>
    );
};
